import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ShoppingBag, Loader2, Minus, Plus, Trash2 } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { finishImage } from "@/lib/finishImages";
import { toast } from "sonner";

export function CartDrawer() {
  const [open, setOpen] = useState(false);
  const {
    items,
    isLoading,
    isSyncing,
    syncCart,
    updateQuantity,
    removeItem,
    getCheckoutUrl,
  } = useCartStore();
  const totalItems = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce(
    (s, i) => s + parseFloat(i.price.amount) * i.quantity,
    0,
  );
  const currency = items[0]?.price.currencyCode || "USD";

  useEffect(() => {
    syncCart();
  }, [syncCart]);

  useEffect(() => {
    if (open) syncCart();
  }, [open, syncCart]);

  const busy = isLoading || isSyncing;

  const handleCheckout = async () => {
    if (totalItems === 0) {
      toast("Your bag is empty", { description: "Add a piece to start your pre-order." });
      return;
    }
    await syncCart();
    const url = getCheckoutUrl();
    if (url) {
      window.location.assign(url);
      return;
    }
    toast.error("Checkout unavailable", { description: "Please try again in a moment." });
  };


  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          disabled={busy && totalItems === 0}
          aria-label={totalItems > 0 ? `Open bag (${totalItems} items)` : "Bag is empty"}
          className="relative text-foreground hover:bg-transparent"
        >
          {busy && totalItems === 0 ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <ShoppingBag className="h-5 w-5" />
          )}
          {totalItems > 0 && (
            <Badge className="absolute -top-1 -right-1 h-4 w-4 rounded-full p-0 flex items-center justify-center text-[10px] bg-accent text-accent-foreground">
              {totalItems}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle className="font-mono text-[11px] uppercase tracking-[0.3em]">
            Your Bag
          </SheetTitle>
          <SheetDescription className="font-mono text-[11px] uppercase tracking-[0.2em]">
            {totalItems === 0
              ? "Empty"
              : `${totalItems} item${totalItems !== 1 ? "s" : ""}`}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 mt-6">
          {items.length === 0 ? (
            <div className="h-full flex items-center justify-center text-center">
              <div>
                <ShoppingBag className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  Your bag is empty
                </p>
              </div>
            </div>
          ) : (
            <ul className="space-y-6">
              {items.map((item) => {
                const shopifyImg = item.product.node.images?.edges?.[0]?.node;
                const variantLabel =
                  item.selectedOptions.map((o) => o.value).join(" ") || item.variantTitle || "";
                const finishSrc = finishImage(variantLabel);
                const imgSrc = finishSrc || shopifyImg?.url;
                const imgAlt = finishSrc
                  ? `${item.product.node.title} — ${variantLabel}`
                  : shopifyImg?.altText || item.product.node.title;
                return (
                  <li key={item.variantId} className="flex gap-4 border-b border-border pb-6">
                    <div className="w-20 h-20 rounded-xl bg-muted flex-shrink-0 overflow-hidden flex items-center justify-center p-2">
                      {imgSrc && (
                        <img
                          src={imgSrc}
                          alt={imgAlt}
                          className="max-w-full max-h-full object-contain"
                        />
                      )}
                    </div>


                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-serif text-base truncate">
                            {item.product.node.title}
                          </p>
                          {item.selectedOptions.length > 0 && (
                            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                              {item.selectedOptions.map((o) => o.value).join(" · ")}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => removeItem(item.variantId)}
                          aria-label="Remove"
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <div className="inline-flex items-center border border-border">
                          <button
                            onClick={() =>
                              updateQuantity(item.variantId, item.quantity - 1)
                            }
                            className="w-8 h-8 flex items-center justify-center hover:bg-muted disabled:opacity-40"
                            disabled={isLoading}
                            aria-label="Decrease"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="w-8 text-center font-mono text-xs">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() =>
                              updateQuantity(item.variantId, item.quantity + 1)
                            }
                            className="w-8 h-8 flex items-center justify-center hover:bg-muted disabled:opacity-40"
                            disabled={isLoading}
                            aria-label="Increase"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        <p className="font-mono text-xs">
                          {item.price.currencyCode}{" "}
                          {(parseFloat(item.price.amount) * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-border pt-6 mt-6 space-y-4">
            <div className="flex justify-between font-mono text-xs uppercase tracking-[0.2em]">
              <span>Subtotal</span>
              <span>
                {currency} {subtotal.toFixed(2)}
              </span>
            </div>
            <button
              onClick={handleCheckout}
              disabled={busy}
              className="w-full h-12 bg-foreground text-background font-mono text-xs uppercase tracking-[0.25em] hover:bg-foreground/90 disabled:opacity-40"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin mx-auto" />
              ) : (
                "Checkout"
              )}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
